import type { ClinicianType, SpecialityMatchType } from "@src/constants/filter-constants";
import {
  PROVIDER_SEARCH_SORT_VALUES,
  PROVIDER_SORT_OPTIONS,
} from "@src/constants/provider-search-sort.constants";
import type { SharedData } from "@src/context";
import { callApi } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import {
  isPrescriberClinicianType,
  resolveTherapyTypesForRequest,
} from "@src/utils/provider-search-utils";
import { resolveModalityFromAgeAndSessionType } from "@src/utils/session-type-modality";
import ClientLogsService from "./client-logs.service";
import { buildProviderSearchConfig } from "./configs";
import type { ProviderSortBy } from "./configs/provider-search.config";
import { runManagedRequest } from "./request-manager";

interface ProviderParams {
  user_uuid?: string;
  context_id?: number;
  state?: string;
  user_latitude?: number;
  user_longitude?: number;
  packageid?: string;
  modality?: string;
  ageserved?: string | number;
  languages?: string;
  // typeModalities?: any;
  specialities?: string[];
  specialityMatchType?: SpecialityMatchType;
  therapytypes?: string;
  gender?: string;
  max_distance?: string | number | null;
  clinician_type?: ClinicianType;
  dayOfWeek?: string[];
  startHour?: number;
  endHour?: number;
  sort_by?: ProviderSortBy;
  sort_zipcode?: string;
}

const DEFAULT_SPECIALITY_MATCH_TYPE: SpecialityMatchType = "or";

const normalizeSpecialityMatchType = (value?: string): SpecialityMatchType => {
  return value === "and" ? "and" : DEFAULT_SPECIALITY_MATCH_TYPE;
};

export function buildProviderSearchRequest(
  appData: Partial<SharedData> = {},
  overrides: Partial<ProviderParams> = {},
): ProviderParams {
  const defaults: ProviderParams = {
    user_uuid: appData?.userUuid,
    context_id: appData?.address?.context_id,
    state: appData?.address?.state_code,
    user_latitude: appData?.address?.position?.lat,
    user_longitude: appData?.address?.position?.lon,
    packageid: appData?.insurancePolicy?.id,
    modality: resolveModalityFromAgeAndSessionType(appData?.ageRange, appData?.sessionType),
    ageserved: appData?.ageRange,
    languages: appData?.languages,
    specialities: appData?.specialities,
    specialityMatchType: normalizeSpecialityMatchType(appData?.specialityMatchType),
    therapytypes: resolveTherapyTypesForRequest(appData?.providerType, appData?.careFormat),
    gender: appData?.providerGender,
    max_distance: appData?.providerRadius ? parseInt(appData?.providerRadius, 10) : null,
    clinician_type: appData?.providerType,
    dayOfWeek: appData?.dayOfWeek,
    startHour: appData?.startHour,
    endHour: appData?.endHour,
  };

  // Apply overrides only for defined values (preserve defaults for undefined overrides)
  const definedOverrides = Object.fromEntries(
    Object.entries(overrides).filter(([, value]) => value !== undefined),
  ) as Partial<ProviderParams>;

  const mergedRequest: ProviderParams = { ...defaults, ...definedOverrides };

  // Prescriber searches must never send therapytypes, even if a caller provides it via overrides.
  if (isPrescriberClinicianType(mergedRequest.clinician_type)) {
    return {
      ...mergedRequest,
      therapytypes: undefined,
    };
  }

  return mergedRequest;
}

// Shared by ProviderList's sort dropdown and the SecondaryFilters apply/reset flow so both
// request paths stay in sync when "Distance" sort is active.
export function getProviderSearchSortOverrides(
  selectedSortBy: string,
  appData: Partial<SharedData>,
): Partial<ProviderParams> {
  if (selectedSortBy !== PROVIDER_SORT_OPTIONS.NEAREST_TO_FURTHEST) {
    return {};
  }

  const sortZipcode = appData.zip?.trim();

  return {
    sort_by: PROVIDER_SEARCH_SORT_VALUES.NEAREST_TO_FARTHEST,
    ...(sortZipcode ? { sort_zipcode: sortZipcode } : {}),
  };
}

// biome-ignore lint/suspicious/noExplicitAny: provider-search passes through untyped API JSON
export async function getProviderList(params: ProviderParams): Promise<any> {
  const sanitizedParams: ProviderParams = {
    ...params,
    specialityMatchType: normalizeSpecialityMatchType(params.specialityMatchType),
    therapytypes: isPrescriberClinicianType(params.clinician_type)
      ? undefined
      : params.therapytypes,
  };

  return runManagedRequest<any>({
    key: `provider-search:${sanitizedParams.user_uuid ?? "anon"}`,
    policy: "latest-wins",
    request: async (signal) => {
      const { data, response } = await callApi(buildProviderSearchConfig(sanitizedParams, signal));

      if (!response.ok) {
        // 4xx = this app sent a bad request (CallerError); 5xx = the downstream
        // provider search dependency failed (DependencyFailure).
        const errorType = getFailureTypeForHttpStatus(response.status);
        ClientLogsService.logError(
          sanitizedParams.user_uuid || "unknown",
          "provider-search.service.getProviderList",
          "Error fetching provider list",
          new Error(`HTTP ${response.status}: ${response.statusText}`),
          {
            errorType,
            dependencyName: "ProviderSearchAPI",
            functionName: "getProviderList",
          },
        );
        const err = data as { message?: string } | null;
        return { error: err?.message || "Failed to fetch provider list" };
      }
      return data;
    },
  });
}
